import React, { useState, useMemo, useRef } from 'react';
import {
  TrendingUpOutlined,
  PeopleOutlined,
  ApiOutlined,
  SearchOutlined,
  CloseOutlined,
  RefreshOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined,
} from '@mui/icons-material';
import StatCard                from '../../atoms/StatCard/StatCard';
import ExportButton            from '../../atoms/ExportButton/ExportButton';
import DateRangePicker         from '../../molecules/DateRangePicker/DateRangePicker';
import ViewSwitcher            from '../../atoms/ViewSwitcher/ViewSwitcher';
import AdminHierarchyFilter    from '../../atoms/AdminHierarchyFilter/AdminHierarchyFilter';
import ActivityInsightsChart   from '../../molecules/charts/ActivityInsightsChart/ActivityInsightsChart';
import DonutChart              from '../../molecules/charts/DonutChart/DonutChart';
import TopUsersChart           from '../../molecules/charts/TopUsersChart/TopUsersChart';
import UserActivityDetailChart from '../../molecules/charts/UserActivityDetailChart/UserActivityDetailChart';
import { translateEndpoint }   from '../../../utils/logTranslations';
import { exportEchartsPNG, exportNodeAsPNG, exportRowsAsCSV, exportPageAsPDF } from '../../../utils/exportChartsUtils';
import './UserLogs.css';

export const AUTH_COLOR = '#0EA5E9';

const VIEW_MODES = [
  { key: 'daily',   label: 'Jour' },
  { key: 'weekly',  label: 'Semaine' },
  { key: 'monthly', label: 'Mois' },
];

const UserLogs = ({
  data,
  loading,
  startDate,
  endDate,
  onDateChange,
  selectedUserId,
  onUserSelect,
  userDetailData,
  loadingUserDetail,
  admins = [],
  selectedAdminId,
  onAdminSelect,
  onRefresh,
  refreshing = false,
  notifSlot,
  isInternal,
  onIsInternalChange,
}) => {
  const [viewMode,   setViewMode]   = useState('daily');
  const [userSearch, setUserSearch] = useState('');

  const pageRef           = useRef(null);
  const activityChartRef  = useRef(null);
  const donutChartRef     = useRef(null);
  const topUsersNodeRef   = useRef(null);
  const userDetailNodeRef = useRef(null);

  const selectedUser = useMemo(() =>
    data?.all_users?.find((u) => u.user_id === selectedUserId),
    [data, selectedUserId],
  );

  const { totalActivities, activeUsers, topFunc } = useMemo(() => {
    const rawTopFunc =
      data?.by_functionality?.[0]?.functionality ||
      data?.by_functionality?.[0]?.endpoint ||
      null;
    return {
      totalActivities: data?.summary?.total_activities ?? 0,
      activeUsers:     data?.summary?.active_users     ?? 0,
      topFunc: rawTopFunc ? translateEndpoint(rawTopFunc, 'auth') : 'N/A',
    };
  }, [data]);

  const donutEndpoints = useMemo(() =>
    (data?.by_functionality || [])
      .map((f) => {
        const raw = f.functionality || f.endpoint || '';
        return {
          endpoint:    translateEndpoint(raw, 'auth'),
          total_usage: f.total_usage || f.count || 0,
        };
      })
      .filter((e) => e.total_usage > 0),
    [data],
  );

  const adaptedMicroservices = useMemo(() => {
    const fromApi = (data?.by_microservice || []).map((ms) => ({ ...ms, color: AUTH_COLOR }));
    if (fromApi.length > 0) return fromApi;
    return [{
      microservice: 'auth',
      total_usage:  data?.summary?.total_activities ?? totalActivities,
      color:        AUTH_COLOR,
    }];
  }, [data, totalActivities]);

  const selectedAdminName = admins.find((a) => a.id === selectedAdminId)?.username || null;

  // ── Handlers d'export ────────────────────────────────────────────
  const handleExportTrendPNG = () => exportEchartsPNG(activityChartRef, 'evolution-temporelle-auth');
  const handleExportTrendCSV = () => exportRowsAsCSV(
    (data?.daily_trend || []).map((d) => ({
      date: d.date || d.day || '',
      total_activites: d.total_activities ?? d.count ?? 0,
    })),
    'evolution-temporelle-auth',
  );

  const handleExportDonutPNG = () => exportEchartsPNG(donutChartRef, 'repartition-endpoints-auth');
  const handleExportDonutCSV = () => exportRowsAsCSV(
    donutEndpoints.map((e) => ({ endpoint: e.endpoint, appels: e.total_usage })),
    'repartition-endpoints-auth',
  );

  const handleExportTopUsersPNG = () => exportNodeAsPNG(topUsersNodeRef.current, 'classement-utilisateurs');
  const handleExportTopUsersCSV = () => exportRowsAsCSV(
    (data?.all_users || []).map((u) => ({
      utilisateur: u.username || `User ${u.user_id}`,
      activite: u.activity_count || 0,
      statut: u.active ? 'actif' : 'inactif',
    })),
    'classement-utilisateurs',
  );

  const handleExportUserDetailPNG = () => exportNodeAsPNG(userDetailNodeRef.current, `profil-activite-${selectedUser?.username || selectedUserId || 'utilisateur'}`);
  const handleExportUserDetailCSV = () => exportRowsAsCSV(
    (userDetailData?.by_functionality || []).map((f) => ({
      endpoint: translateEndpoint(f.functionality || f.endpoint || '', 'auth'),
      appels: f.total_usage || f.count || 0,
    })),
    `profil-activite-${selectedUser?.username || selectedUserId || 'utilisateur'}`,
  );

  const handleExportPDF = () => exportPageAsPDF(pageRef.current, 'logs-authentification');

  if (loading && !refreshing) {
    return (
      <div className="ul-loading">
        <div className="ul-spinner" />
        <p>Chargement des logs utilisateurs …</p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="ul-wrapper" ref={pageRef}>

      {/* ── HEADER ── */}
      <div className="ul-header">

        {/* Bloc KPIs */}
        <div className="ul-stats-block">
          {/* <span className="ul-stats-label">Statistiques :</span> */}
          <div className="ul-kpis-row">
            <div className="ul-kpi-item">
              <StatCard icon={<TrendingUpOutlined />} label="Total Activités" value={totalActivities.toLocaleString()} variant="red" />
            </div>
            {/* <div className="ul-kpi-item">
              <StatCard icon={<PeopleOutlined />} label="Utilisateurs actifs" value={activeUsers} variant="blue" />
            </div> */}
            <div className="ul-kpi-item ul-kpi-item--full">
              <StatCard icon={<ApiOutlined />} label="Top fonctionnalité" value={topFunc} variant="orange" small />
            </div>
          </div>
        </div>

        <div className="header-sep" />

        {/* Bloc filtres */}
        <div className="ul-filters-block">
          <div className="ul-filter-group ul-filter-group--admin">
            <span className="ul-filter-label">Admin :</span>
            <AdminHierarchyFilter
              admins={admins}
              selectedAdminId={selectedAdminId}
              onSelect={onAdminSelect}
            />
          </div>

          <div className="ul-filter-group ul-filter-group--comptes">
            <span className="ul-filter-label">Comptes :</span>
            <select
              value={isInternal === null ? 'all' : isInternal ? 'internal' : 'external'}
              onChange={(e) => {
                const v = e.target.value;
                onIsInternalChange(v === 'all' ? null : v === 'internal' ? true : false);
              }}
              className="ul-internal-select"
            >
              <option value="all">Tous</option>
              <option value="external">Externes</option>
              <option value="internal">Internes</option>
            </select>
          </div>

          <div className="ul-filter-group ul-filter-group--date">
            <span className="ul-filter-label">Période :</span>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartChange={(date) => onDateChange(date, endDate)}
              onEndChange={(date)   => onDateChange(startDate, date)}
            />
          </div>
        </div>

        <div className="header-sep" />

        {/* Bloc actions : export PDF + refresh + notification */}
        <div className="ul-actions-slot">
          {/* <button
            className="ul-refresh-btn"
            onClick={handleExportPDF}
            title="Exporter la page en PDF"
          >
            <PictureAsPdfOutlined style={{ fontSize: 14 }} />
            <span>PDF</span>
          </button> */}
          <button
            className={`ul-refresh-btn${refreshing ? ' ul-refresh-btn--loading' : ''}`}
            onClick={onRefresh}
            disabled={refreshing}
            title="Rafraîchir"
          >
            <RefreshOutlined style={{ fontSize: 14 }} />
            <span>Rafraîchir</span>
          </button>
          {notifSlot}
        </div>

      </div>

      {/* Admin badge */}
      {selectedAdminId && selectedAdminName && (
        <div className="ul-admin-badge">
          <span className="ul-admin-badge-dot" />
          <span className="ul-admin-badge-text">
            Données filtrées · hiérarchie de <strong>{selectedAdminName}</strong>
          </span>
        </div>
      )}

      {/* is_internal badge */}
      {isInternal !== null && (
        <div className="ul-admin-badge" style={{ background: isInternal ? '#f0fdf4' : '#eff6ff', borderColor: isInternal ? '#86efac' : '#93c5fd' }}>
          <span className="ul-admin-badge-dot" style={{ background: isInternal ? '#16a34a' : '#2563eb' }} />
          <span className="ul-admin-badge-text">
            Filtre actif · comptes <strong style={{ color: isInternal ? '#16a34a' : '#2563eb' }}>{isInternal ? 'internes' : 'externes'}</strong> uniquement
          </span>
        </div>
      )}

      {/* Tendance d'utilisation */}
      <div className="ul-section-label">Tendance d'utilisation</div>
      <div className="ul-top-row">
        <div className="ul-chart-card ul-top-card">
          <div className="ul-chart-header--row">
            <div>
              <h3 className="ul-chart-title">Évolution temporelle</h3>
              <span className="ul-chart-subtitle">Microservice d'authentification</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ViewSwitcher
                modes={VIEW_MODES}
                value={viewMode}
                onChange={setViewMode}
                color={AUTH_COLOR}
              />
              {/* <span className="chart-export-cluster">
                <ExportButton onExport={handleExportTrendPNG} title="Exporter en PNG" />
                <ExportButton onExport={handleExportTrendCSV} title="Exporter en CSV" icon={TableChartOutlined} />
              </span> */}
            </div>
          </div>
          <div className="ul-chart-body">
            <ActivityInsightsChart
              ref={activityChartRef}
              dailyTrend={(data.daily_trend || []).map(d => {
                const rawDate = d.date || d.day || d.timestamp?.slice(0, 10) || '';
                const [y, mo, day] = rawDate.split('-').map(Number);
                const localDate = y
                  ? `${y}-${String(mo).padStart(2,'0')}-${String(day).padStart(2,'0')}`
                  : rawDate;
                return {
                  ...d,
                  date: localDate,
                  total_activities: d.total_activities ?? d.count ?? d.total ?? 0,
                };
              })}
              microservices={adaptedMicroservices}
              startDate={startDate}
              endDate={endDate}
              viewMode={viewMode}
              onViewChange={setViewMode}
            />
          </div>
        </div>

        <div className="ul-chart-card ul-top-card">
          <div className="ul-chart-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <h3 className="ul-chart-title">Répartition des endpoints</h3>
              <span className="ul-chart-subtitle">
                Répartition par endpoint dans le microservice d'authentification
              </span>
            </div>
            {/* <span className="chart-export-cluster">
              <ExportButton onExport={handleExportDonutPNG} title="Exporter en PNG" />
              <ExportButton onExport={handleExportDonutCSV} title="Exporter en CSV" icon={TableChartOutlined} />
            </span> */}
          </div>
          <div className="ul-chart-body ul-donut-body">
            <DonutChart
              ref={donutChartRef}
              mode="endpoint"
              endpoints={donutEndpoints}
              totalCalls={totalActivities}
              color={AUTH_COLOR}
            />
          </div>
        </div>
      </div>

      {/* Détail utilisateurs */}
      <div className="ul-section-label">Détail utilisateurs</div>
      <div className="ul-bottom-row">
        <div className="ul-chart-card">
          <div className="ul-chart-header ul-chart-header--search">
            <div className="ul-chart-header-left">
              <h3 className="ul-chart-title">Classement des utilisateurs</h3>
              <span className="ul-chart-subtitle">
                {data.summary?.total_users} membres · cliquez pour les détails
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* <span className="chart-export-cluster">
                <ExportButton onExport={handleExportTopUsersPNG} title="Exporter en PNG" />
                <ExportButton onExport={handleExportTopUsersCSV} title="Exporter en CSV" icon={TableChartOutlined} />
              </span> */}
              <div className={`ul-user-search${userSearch ? ' ul-user-search--active' : ''}`}>
                <SearchOutlined className="ul-user-search__icon" />
                <input
                  type="text"
                  className="ul-user-search__input"
                  placeholder="Nom, email ou organisation…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                {userSearch && (
                  <button
                    className="ul-user-search__clear"
                    onClick={() => setUserSearch('')}
                    title="Effacer"
                  >
                    <CloseOutlined style={{ fontSize: 13 }} />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="ul-chart-body ul-chart-body--scroll" ref={topUsersNodeRef}>
            <TopUsersChart
              users={data.all_users}
              totalActivities={totalActivities}
              onUserSelect={onUserSelect}
              selectedUserId={selectedUserId}
              searchQuery={userSearch}
            />
          </div>
        </div>

        <div className="ul-chart-card">
          <div className="ul-chart-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <h3 className="ul-chart-title">Profil d'activité</h3>
              {selectedUser && (
                <span className="ul-chart-subtitle">
                  {selectedUser.username || `User ${selectedUser.user_id}`}
                </span>
              )}
            </div>
            {/* <span className="chart-export-cluster">
              <ExportButton onExport={handleExportUserDetailPNG} title="Exporter en PNG" disabled={!selectedUserId} />
              <ExportButton onExport={handleExportUserDetailCSV} title="Exporter en CSV" icon={TableChartOutlined} disabled={!selectedUserId} />
            </span> */}
          </div>
          <div className="ul-chart-body ul-chart-body--scroll" ref={userDetailNodeRef}>
            <UserActivityDetailChart
              userId={selectedUserId}
              username={selectedUser?.username || `User ${selectedUserId}`}
              activityData={userDetailData}
              loading={loadingUserDetail}
            />
          </div>
        </div>
      </div>

    </div>
  );
};

export default UserLogs;